import React, { useReducer, useEffect, useCallback, useRef } from 'react';
import { WizardCompletionContext, WizardCompletionContextValue, TabProgress } from './complexContexts';
import { logger } from '../utils/logger';

// Types for completion state
export interface TabCompletionState {
  [stepIndex: number]: boolean;
}

export interface CompletionState {
  [catalogId: string]: {
    upload: TabCompletionState;
    describe: TabCompletionState;
    media: TabCompletionState;
    measure: TabCompletionState;
    catalogKeywords: TabCompletionState;
    provenance: TabCompletionState;
    additionalInfo: TabCompletionState;
  };
}

// Re-export types for backwards compatibility
export type { TabProgress, WizardCompletionContextValue };

const STORAGE_KEY_PREFIX = 'wizard-completion-';

// Step counts per tab (matches TAB_STEPS in UploadObject.tsx)
const TAB_STEP_COUNTS: { [key: string]: number } = {
  upload: 2,
  describe: 4,
  media: 4,
  measure: 1,
  catalogKeywords: 3, // Catalog Number, Keywords, Collection Catalogs
  provenance: 5, // Provenance, Exhibitions, Publications, Awards & Honors, Related Works
  additionalInfo: 2 // Notes, Location and Value
};

// Sanitize loaded state: ensures all tab keys exist and strips out-of-range step indices.
// This prevents stale localStorage data (e.g., old 7-key describe from pre-media-split)
// from inflating completion counts.
function sanitizeCompletionState(
  loaded: Record<string, TabCompletionState>
): CompletionState[string] {
  const defaultState: CompletionState[string] = {
    upload: {}, describe: {}, media: {}, measure: {},
    catalogKeywords: {}, provenance: {}, additionalInfo: {}
  };
  const sanitized = { ...defaultState };
  for (const [tab, maxSteps] of Object.entries(TAB_STEP_COUNTS)) {
    const tabData = loaded[tab] || {};
    const cleaned: TabCompletionState = {};
    for (let i = 0; i < maxSteps; i++) {
      if (tabData[i] !== undefined) {
        cleaned[i] = tabData[i];
      }
    }
    sanitized[tab as keyof typeof sanitized] = cleaned;
  }
  return sanitized;
}

// Reducer action types
type CompletionAction =
  | { type: 'LOAD_CATALOG'; catalogId: string; state: CompletionState[string] }
  | { type: 'UPDATE_STEP'; catalogId: string; tab: string; stepIndex: number; completed: boolean }
  | { type: 'BATCH_UPDATE_TAB'; catalogId: string; tab: string; steps: Record<number, boolean> }
  | { type: 'BULK_INITIALIZE'; catalogId: string; tabsData: Record<string, Record<number, boolean>> }
  | { type: 'RESET_CATALOG'; catalogId: string };

// Reducer function
function completionReducer(state: CompletionState, action: CompletionAction): CompletionState {
  switch (action.type) {
    case 'LOAD_CATALOG':
      return {
        ...state,
        [action.catalogId]: action.state
      };

    case 'UPDATE_STEP': {
      const current = state[action.catalogId] || {
        upload: {},
        describe: {},
        media: {},
        measure: {},
        catalogKeywords: {},
        provenance: {},
        additionalInfo: {}
      };

      return {
        ...state,
        [action.catalogId]: {
          ...current,
          [action.tab]: {
            ...current[action.tab as keyof typeof current],
            [action.stepIndex]: action.completed
          }
        }
      };
    }

    // Merges into existing tab state (used by individual tab autosave).
    // Contrast with BULK_INITIALIZE which fully replaces tab state.
    case 'BATCH_UPDATE_TAB': {
      const current = state[action.catalogId] || {
        upload: {},
        describe: {},
        media: {},
        measure: {},
        catalogKeywords: {},
        provenance: {},
        additionalInfo: {}
      };

      return {
        ...state,
        [action.catalogId]: {
          ...current,
          [action.tab]: {
            ...current[action.tab as keyof typeof current],
            ...action.steps
          }
        }
      };
    }

    // Fully replaces tab state (used by EditModeCompletionInitializer).
    // Ensures stale keys from pre-refactor localStorage are discarded.
    case 'BULK_INITIALIZE': {
      const current = state[action.catalogId] || {
        upload: {},
        describe: {},
        media: {},
        measure: {},
        catalogKeywords: {},
        provenance: {},
        additionalInfo: {}
      };

      const newTabState = { ...current };
      for (const [tab, steps] of Object.entries(action.tabsData)) {
        newTabState[tab as keyof typeof current] = steps;
      }

      return {
        ...state,
        [action.catalogId]: newTabState
      };
    }

    case 'RESET_CATALOG': {
      const updated = { ...state };
      delete updated[action.catalogId];
      return updated;
    }

    default:
      return state;
  }
}

interface WizardCompletionProviderProps {
  catalogId: string;
  children: React.ReactNode;
}

export const WizardCompletionProvider: React.FC<WizardCompletionProviderProps> = ({
  catalogId,
  children
}) => {
  const [completionState, dispatch] = useReducer(completionReducer, {});
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevCatalogIdRef = useRef<string | null>(null);

  // Load completion state from localStorage when catalogId changes
  useEffect(() => {
    if (!catalogId || prevCatalogIdRef.current === catalogId) return;

    prevCatalogIdRef.current = catalogId;

    const storageKey = `${STORAGE_KEY_PREFIX}${catalogId}`;
    const stored = localStorage.getItem(storageKey);

    let newState: CompletionState[string];

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        newState = sanitizeCompletionState(parsed);
      } catch (error) {
        logger.debug('Failed to parse wizard completion state from localStorage', error);
        newState = {
          upload: {},
          describe: {},
          media: {},
          measure: {},
          catalogKeywords: {},
          provenance: {},
          additionalInfo: {}
        };
      }
    } else {
      // Initialize empty state for this catalog
      newState = {
        upload: {},
        describe: {},
        media: {},
        measure: {},
        catalogKeywords: {},
        provenance: {},
        additionalInfo: {}
      };
    }

    // Dispatch action to load catalog state
    // Using reducer pattern prevents race conditions and follows React best practices
    dispatch({ type: 'LOAD_CATALOG', catalogId, state: newState });
  }, [catalogId]);

  // Save completion state to localStorage when it changes (debounced)
  // This effect ensures the complete reducer state is saved, avoiding race conditions
  // when multiple markStepComplete calls happen rapidly (e.g., updateCompletionTracking)
  useEffect(() => {
    if (!catalogId || !completionState[catalogId]) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounced save (1 second)
    saveTimeoutRef.current = setTimeout(() => {
      const storageKey = `${STORAGE_KEY_PREFIX}${catalogId}`;
      try {
        localStorage.setItem(storageKey, JSON.stringify(completionState[catalogId]));
      } catch (error) {
        logger.error('Failed to save completion state to localStorage', error);
      }
    }, 1000);
  }, [catalogId, completionState]);

  // Mark step as complete or incomplete
  const markStepComplete = useCallback((tab: string, stepIndex: number, completed: boolean) => {
    if (!catalogId) return;

    // Check if value actually changed to prevent unnecessary re-renders
    const current = completionState[catalogId] || {
      upload: {},
      describe: {},
      media: {},
      measure: {},
      catalogKeywords: {},
      provenance: {},
      additionalInfo: {}
    };
    const currentValue = current[tab as keyof typeof current]?.[stepIndex];
    if (currentValue === completed) {
      return; // No change, skip update to prevent re-render loops
    }

    // Dispatch action to update step
    // The useEffect above will save the updated state to localStorage
    dispatch({ type: 'UPDATE_STEP', catalogId, tab, stepIndex, completed });
  }, [catalogId, completionState]);

  // Batch update multiple steps in a tab atomically
  // This reduces state updates from N to 1 when loading data with multiple completed steps
  const batchUpdateTab = useCallback((tab: string, steps: Record<number, boolean>) => {
    if (!catalogId) return;

    // Dispatch single action to update all steps at once
    dispatch({ type: 'BATCH_UPDATE_TAB', catalogId, tab, steps });
  }, [catalogId]);

  // Bulk initialize all tabs atomically (used when entering edit mode)
  const bulkInitialize = useCallback((tabsData: Record<string, Record<number, boolean>>) => {
    if (!catalogId) return;

    dispatch({ type: 'BULK_INITIALIZE', catalogId, tabsData });
  }, [catalogId]);

  // Check if step is complete
  const isStepComplete = useCallback((tab: string, stepIndex: number): boolean => {
    if (!catalogId || !completionState[catalogId]) return false;

    const tabState = completionState[catalogId][tab as keyof CompletionState[string]];
    return tabState ? tabState[stepIndex] === true : false;
  }, [catalogId, completionState]);

  // Get progress for a tab (completed / total)
  const getTabProgress = useCallback((tab: string): TabProgress => {
    if (!catalogId || !completionState[catalogId]) {
      return { completed: 0, total: TAB_STEP_COUNTS[tab] || 0 };
    }

    const tabState = completionState[catalogId][tab as keyof CompletionState[string]];
    const total = TAB_STEP_COUNTS[tab] || 0;
    const completed = tabState
      ? Object.entries(tabState)
          .filter(([key, value]) => {
            const idx = Number(key);
            return idx >= 0 && idx < total && value === true;
          })
          .length
      : 0;

    return { completed, total };
  }, [catalogId, completionState]);

  // Reset completion state for a catalog
  const resetCompletion = useCallback((targetCatalogId: string) => {
    // Dispatch action to reset catalog
    dispatch({ type: 'RESET_CATALOG', catalogId: targetCatalogId });

    // Remove from localStorage
    const storageKey = `${STORAGE_KEY_PREFIX}${targetCatalogId}`;
    localStorage.removeItem(storageKey);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const contextValue: WizardCompletionContextValue = {
    markStepComplete,
    batchUpdateTab,
    bulkInitialize,
    isStepComplete,
    getTabProgress,
    resetCompletion
  };

  return (
    <WizardCompletionContext.Provider value={contextValue}>
      {children}
    </WizardCompletionContext.Provider>
  );
};

// Note: useWizardCompletion hook is in hooks/useWizardCompletion.ts for fast refresh compatibility
